import { z } from "zod";
import { BCP47Schema } from "../enums";
import { E164_REGEX } from "../phone";
import {
  OTA_PLATFORM_IDS,
  type PlatformId,
  type ProviderId,
} from "../platforms";

// API-boundary contracts for the property-integrations endpoints (messaging +
// OTA): list, phone link, WhatsApp connect-session, and the channel-manager
// connect flow. Plain zod (no drizzle / pg-core) so the mobile bundle stays
// decoupled from the DB package. Both the API route handlers and the mobile
// api/ client import these, so request/response shapes can't drift.

// Platform / provider live in the @vidimo/shared matrix; reuse those id
// unions for the typed fields rather than redefining them here. Compile-time
// aliases only — nothing in this contract validates a platform/provider id at
// the wire boundary (OtaPlatformSchema below covers its own two-value subset).
export type IntegrationPlatform = PlatformId;
export type IntegrationProvider = ProviderId;

// Connection lifecycle, surfaced on the Settings cards.
export const IntegrationStatusSchema = z.enum([
  "pending",
  "active",
  "disabled",
  "error",
]);
export type IntegrationStatus = z.infer<typeof IntegrationStatusSchema>;

// Subset of platforms we offer a distribution connection for. Built from the
// matrix's own tuple rather than re-typed here, so the two spellings cannot
// clash.
export const OtaPlatformSchema = z.enum(OTA_PLATFORM_IDS);

// POST /property-integrations/phone.
export const LinkPhoneInputSchema = z.object({
  e164: z.string().regex(E164_REGEX, "Expected E.164 phone number"),
  twilioNumberSid: z.string().min(1).optional(),
});
export type LinkPhoneInput = z.infer<typeof LinkPhoneInputSchema>;

export const LinkPhoneResponseSchema = z.object({
  integrationId: z.string(),
  success: z.boolean(),
});
export type LinkPhoneResponse = z.infer<typeof LinkPhoneResponseSchema>;

// Where a connect flow was started: the native app, the desktop app, or the web
// app in a browser. The flow ends in a browser either way, and only this tells
// its last page how "Return to Vidimo" hands the host back. The connect token
// carries it when the app said; nothing trusts it for more than that.
export const ConnectClientSchema = z.enum(["native", "desktop", "web"]);
export type ConnectClient = z.infer<typeof ConnectClientSchema>;

// POST /whatsapp/connect-session and /instagram/connect-session. The
// body is optional: `mode` tunes the page's copy, `client` is above, and
// `lang` is the app's interface language, which the connect origin cannot
// read from the app's storage, so its page gets it on its link.
export const ConnectSessionInputSchema = z.object({
  client: ConnectClientSchema.optional(),
  lang: BCP47Schema.optional(),
  mode: z.enum(["connect", "reconnect"]).optional(),
});
export type ConnectSessionInput = z.infer<typeof ConnectSessionInputSchema>;

// POST /whatsapp/connect-session adds the number being connected, which rides
// the page's link as `WHATSAPP_CONNECT_NUMBER_PARAM`. Optional: an app bundle
// that predates it sends none, and its page then asks for a fresh link. A
// malformed one is dropped alone, so the body's other fields still count.
export const WhatsAppConnectSessionInputSchema =
  ConnectSessionInputSchema.extend({
    e164: z.string().regex(E164_REGEX).optional().catch(undefined),
  });
export type WhatsAppConnectSessionInput = z.infer<
  typeof WhatsAppConnectSessionInputSchema
>;

export const WHATSAPP_CONNECT_NUMBER_PARAM = "number";

// POST /whatsapp/connect-session: mints the URL of the host app's
// WhatsApp connect page, carrying a short-lived token.
export const WhatsAppConnectSessionResponseSchema = z.object({
  url: z.string(),
});
export type WhatsAppConnectSessionResponse = z.infer<
  typeof WhatsAppConnectSessionResponseSchema
>;

// POST /whatsapp/restore (no body). `restored: false`: nothing live to
// restore, so the app goes on to Meta's signup, unless `held`: a sender that is
// not live still holds the number, and a signup would register it twice.
// The route sends `held` only when it is true.
export const RestoreWhatsAppResponseSchema = z.object({
  held: z.boolean().optional(),
  restored: z.boolean(),
});
export type RestoreWhatsAppResponse = z.infer<
  typeof RestoreWhatsAppResponseSchema
>;

// POST /whatsapp/disconnect (no body). Idempotent: `disconnected` is false
// when nothing was connected.
export const DisconnectWhatsAppResponseSchema = z.object({
  disconnected: z.boolean(),
});
export type DisconnectWhatsAppResponse = z.infer<
  typeof DisconnectWhatsAppResponseSchema
>;

// Why a connect was refused in a way no retry clears, sent as `code` beside the
// error's `error` text like other refusal codes. The app owns the copy and
// offers the Vidimo team for each; `propertyName` names the property that
// holds a number when the caller is a member there. `property_preparing`
// refuses any operational action, not only a connect (ADR-0144).
export const ConnectRefusalCodeSchema = z.enum([
  "number_claimed",
  "account_claimed",
  "setup_broken",
  "subscription_required",
  "property_preparing",
]);
export type ConnectRefusalCode = z.infer<typeof ConnectRefusalCodeSchema>;

export const ConnectRefusalSchema = z.object({
  code: ConnectRefusalCodeSchema,
  propertyName: z.string().optional(),
});
export type ConnectRefusal = z.infer<typeof ConnectRefusalSchema>;

// The host-app pages a connect flow ends on. The API builds its redirects and
// connect-session URLs from these and the app's router mounts them, so both
// sides name one path.
export const CONNECT_PAGE_PATHS = {
  instagram: "/connect/instagram",
  whatsapp: "/connect/whatsapp",
} as const;

// The only API routes the connect pages call. Each is authenticated by the
// connect token in its body, which is why the connect origin may reach them
// without credentials; the API's CORS reads this list, so a route the pages
// start calling has to be named here to work at all.
export const CONNECT_PAGE_API_PATHS = {
  whatsappConnect: "/twilio/whatsapp/connect",
  whatsappSignupConfig: "/twilio/whatsapp/signup-config",
  whatsappStatus: "/twilio/whatsapp/status",
} as const;

// GET /instagram/connect finishes by redirecting to the Instagram connect
// page with one of these as `result`, plus `propertyId` and `client` once the
// signed state has named them. A code rather than text: the page owns the
// copy, and a message carried in the URL is anyone's to rewrite.
export const InstagramConnectResultSchema = z.enum([
  "connected",
  "cancelled",
  "invalid-link",
  "expired",
  "subscription-required",
  "connected-elsewhere",
  "failed",
]);
export type InstagramConnectResult = z.infer<
  typeof InstagramConnectResultSchema
>;

// POST /twilio/whatsapp/signup-config: what the WhatsApp connect page
// needs to launch Meta's Embedded Signup for the property its token names.
// Token-authenticated like /connect and /status, because the page runs in a
// browser that shares no session with the app.
export const WhatsAppSignupConfigInputSchema = z.object({
  token: z.string().min(1),
});
export type WhatsAppSignupConfigInput = z.infer<
  typeof WhatsAppSignupConfigInputSchema
>;

export const WhatsAppSignupConfigResponseSchema = z.object({
  appId: z.string(),
  /** Null when the app that minted the link did not say where it started. */
  client: ConnectClientSchema.nullable(),
  configId: z.string(),
  graphApiVersion: z.string(),
  mode: z.enum(["connect", "reconnect"]),
  propertyId: z.string(),
  propertyName: z.string().nullable(),
  solutionId: z.string(),
});
export type WhatsAppSignupConfigResponse = z.infer<
  typeof WhatsAppSignupConfigResponseSchema
>;

// POST /instagram/disconnect (no body). Removes this property's Instagram
// integration row, which stops DM routing and frees the account's
// (provider, external_id) lock so it can be connected to another property.
// `removed` is 0 when nothing was connected (idempotent).
export const DisconnectInstagramResponseSchema = z.object({
  removed: z.number(),
});
export type DisconnectInstagramResponse = z.infer<
  typeof DisconnectInstagramResponseSchema
>;

// GET /integrations/channex/capability
// Whether this environment offers the two-way channel connection at all.
// The platform matrix cannot answer this on its own: it is compiled into the
// bundle and reads the same on every environment, while the credentials that
// make a connect reachable are per environment. The settings cards ask the
// server, so an environment that cannot serve a connect never offers one.
export const ChannexCapabilityResponseSchema = z.object({
  live: z.boolean(),
});
export type ChannexCapabilityResponse = z.infer<
  typeof ChannexCapabilityResponseSchema
>;

// POST /integrations/channex/:integrationId/connect-session
// A single-use URL into the hosted, white-label channel UI (connect + mapping,
// or the OTA message inbox). The app opens it; the flow runs on the provider.
export const ChannexConnectSessionResponseSchema = z.object({
  url: z.string(),
});
export type ChannexConnectSessionResponse = z.infer<
  typeof ChannexConnectSessionResponseSchema
>;

// POST /integrations/channex/:integrationId/full-sync
// Host-triggered full ARI re-sync: the full booking horizon is enqueued, then
// drained inline. `deferred > 0` (or status "rate_limited") means rate-limit
// budget paused the push partway; the recurring drain finishes it within
// minutes, so it still reads as underway rather than failed.
export const ChannexFullSyncResponseSchema = z.object({
  availabilityCalls: z.number(),
  deferred: z.number(),
  enqueued: z.number(),
  restrictionCalls: z.number(),
  status: z.enum(["ok", "error", "rate_limited"]),
});
export type ChannexFullSyncResponse = z.infer<
  typeof ChannexFullSyncResponseSchema
>;

// POST /integrations/channex/connect
// First-time provisioning: creates the Channex property/room-types/rate-plans
// for the host's selected unit types and inserts the `pending` integration +
// mapping rows. `mappings` is absent when the call reactivated a previously
// disabled row instead (targets are ignored on that path).
export const ChannexConnectInputSchema = z.object({
  platform: OtaPlatformSchema,
  targets: z
    .array(z.object({ unitTypeId: z.string().min(1).max(64) }))
    .min(1)
    .max(50),
});
export type ChannexConnectInput = z.infer<typeof ChannexConnectInputSchema>;

export const ChannexConnectMappingSchema = z.object({
  externalListingName: z.string(),
  id: z.string(),
  unitTypeId: z.string().nullable(),
});

export const ChannexConnectResponseSchema = z.object({
  integrationId: z.string(),
  mappings: z.array(ChannexConnectMappingSchema).optional(),
  reactivated: z.boolean().optional(),
  status: z.literal("pending"),
});
export type ChannexConnectResponse = z.infer<
  typeof ChannexConnectResponseSchema
>;

/**
 * Twilio sender status meaning "Meta sent the ownership code and is waiting
 * for it". Shared because it is the one sender status the app itself has to
 * recognise: it decides whether the integration card shows a code field.
 */
export const SENDER_AWAITING_VERIFICATION = "PENDING_VERIFICATION";

// POST /twilio/whatsapp/verify — the ownership code Meta sends to the
// number being registered. Meta's codes are 6 digits; the bound is a sanity
// check on the input, not a claim about the format, so it stays loose enough
// to survive Meta changing the length.
export const SubmitWhatsAppCodeInputSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{4,10}$/u, "Expected the numeric code from Meta"),
});
export type SubmitWhatsAppCodeInput = z.infer<
  typeof SubmitWhatsAppCodeInputSchema
>;

export const SubmitWhatsAppCodeResponseSchema = z.object({
  /** Twilio's sender status after the submission (ONLINE once accepted). */
  senderStatus: z.string(),
  success: z.literal(true),
});
export type SubmitWhatsAppCodeResponse = z.infer<
  typeof SubmitWhatsAppCodeResponseSchema
>;
